/**
 * TravelDataFields — the travel live-data + content controls the studios'
 * Step-2 travel form composes, as two independently mountable clusters so the
 * flight stays OPT-IN:
 *   - `FlightLookupFields`: flight IATA/date + "Járat ellenőrzése" lookup +
 *     result/error cards;
 *   - `TravelContentFields`: the "Pass tartalom" fieldset
 *     (delayThresholdMinutes, assistancePhone, loungeInfoText, esimLinkUrl).
 * The transit testLat/testLng geofence is intentionally NOT included — it was
 * transit-classType-only and never applied to the Apple path (§3.4). The parent
 * owns useForm + the flight-lookup hook and passes them in. Presentational +
 * controlled (no form-library coupling beyond the passed-in RHF handles).
 */

import { formatAirportLocalDateTime } from '@insurwiz/wallet-core';
import type { FieldErrors, UseFormGetValues, UseFormRegister } from 'react-hook-form';

import { useT } from '../../i18n';

import type { UseFlightLookup } from './useFlightLookup';

// Optional on the TYPE level: the studios' optional-flight schema types them
// `string | undefined`. The cluster itself always reads them defensively.
export interface FlightFieldValues {
  flightIata?: string;
  flightDate?: string;
}

export interface TravelContentFieldValues {
  delayThresholdMinutes: number;
  assistancePhone?: string;
  loungeInfoText?: string;
  esimLinkUrl?: string;
}

export interface FlightLookupFieldsProps<T extends FlightFieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  getValues: UseFormGetValues<T>;
  lookup: UseFlightLookup;
}

export interface TravelContentFieldsProps<T extends TravelContentFieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
}

export function FlightLookupFields<T extends FlightFieldValues>({
  register,
  errors,
  getValues,
  lookup,
}: FlightLookupFieldsProps<T>): JSX.Element {
  const t = useT();
  const { flight, lookupError, retrying, isPending } = lookup;
  const flightIataError = errors.flightIata?.message as string | undefined;
  const flightDateError = errors.flightDate?.message as string | undefined;

  const runLookup = (): void => {
    const v = getValues();
    lookup.lookup(String(v.flightIata ?? ''), String(v.flightDate ?? ''));
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="flightIata" className="label">
            {t('form.flightIata')}
          </label>
          <input
            id="flightIata"
            {...register('flightIata' as never)}
            className="input mt-1 font-mono uppercase"
            placeholder="LH400"
            autoCapitalize="characters"
          />
          {flightIataError ? (
            <div className="text-xs text-red-600 mt-1">{flightIataError}</div>
          ) : null}
        </div>
        <div>
          <label htmlFor="flightDate" className="label">
            {t('form.flightDate')}
          </label>
          <input
            id="flightDate"
            type="date"
            {...register('flightDate' as never)}
            className="input mt-1"
          />
          {flightDateError ? (
            <div className="text-xs text-red-600 mt-1">{flightDateError}</div>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={runLookup}
        disabled={isPending}
        className="btn-secondary w-full"
      >
        {retrying
          ? t('form.lookupRetrying')
          : isPending
            ? t('form.lookupPending')
            : t('form.lookupCta')}
      </button>

      {flight ? (
        // The schedule makes the pick verifiable (right flight, right day) —
        // each time on its own airport's clock, as the pass will show it.
        <div className="rounded-md bg-emerald-50 ring-1 ring-emerald-200 p-3 text-sm text-emerald-900 space-y-1">
          <div>
            <strong>{flight.iata}</strong> — {flight.airline} — {flight.originIata} →{' '}
            {flight.destinationIata}
          </div>
          <div className="text-xs text-emerald-800">
            {t('form.lookupDeparture')}:{' '}
            {formatAirportLocalDateTime(flight.scheduledDepartureUtc, flight.originIata)} (
            {flight.originIata}) · {t('form.lookupArrival')}:{' '}
            {formatAirportLocalDateTime(flight.scheduledArrivalUtc, flight.destinationIata)} (
            {flight.destinationIata})
          </div>
        </div>
      ) : null}
      {lookupError ? (
        <div
          className={
            lookupError.code === 'BUSY'
              ? 'rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900'
              : 'rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800'
          }
        >
          {lookupError.code === 'BUSY' ? (
            <>
              <div className="font-medium mb-1">{t('form.lookupBusyTitle')}</div>
              <div className="text-xs">
                {t('form.lookupBusyBody1')} <em>{t('form.lookupCta')}</em>
                {t('form.lookupBusyBody2')}
              </div>
              <button
                type="button"
                onClick={runLookup}
                disabled={isPending}
                className="mt-2 text-xs underline text-amber-900 hover:text-amber-700"
              >
                {t('form.retryNow')}
              </button>
            </>
          ) : lookupError.code === 'NOT_FOUND' ? (
            <>
              <div className="font-medium mb-1">{t('form.notFoundTitle')}</div>
              <div className="text-xs">{t('form.notFoundBody')}</div>
            </>
          ) : (
            lookupError.message
          )}
        </div>
      ) : null}
    </>
  );
}

export function TravelContentFields<T extends TravelContentFieldValues>({
  register,
  errors,
}: TravelContentFieldsProps<T>): JSX.Element {
  const t = useT();
  const delayError = errors.delayThresholdMinutes?.message as string | undefined;
  const esimError = errors.esimLinkUrl?.message as string | undefined;

  return (
    <fieldset className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 space-y-3">
      <legend className="text-xs font-medium text-slate-700 px-1">{t('form.passContent')}</legend>
      <div>
        <label className="label">{t('form.delayThreshold')}</label>
        <input
          type="number"
          min={15}
          max={600}
          step={15}
          {...register('delayThresholdMinutes' as never, { valueAsNumber: true })}
          className="input mt-1 w-32 font-mono"
        />
        <p className="text-xs text-slate-500 mt-1">{t('form.delayThresholdHint')}</p>
        {delayError ? <div className="text-xs text-red-600 mt-1">{delayError}</div> : null}
      </div>
      <div>
        <label className="label">{t('form.assistPhoneOptional')}</label>
        <input
          type="tel"
          placeholder="+36 1 234 5678"
          {...register('assistancePhone' as never)}
          className="input mt-1"
        />
        <p className="text-xs text-slate-500 mt-1">{t('form.assistPhoneHint')}</p>
      </div>
      <div>
        <label className="label">{t('form.loungeOptional')}</label>
        <textarea
          rows={2}
          placeholder={t('form.loungePlaceholder')}
          {...register('loungeInfoText' as never)}
          className="input mt-1"
        />
      </div>
      <div>
        <label className="label">{t('form.esimOptional')}</label>
        <input
          type="url"
          placeholder="https://partner.example.com/esim"
          {...register('esimLinkUrl' as never)}
          className="input mt-1 font-mono text-xs"
        />
        {esimError ? <div className="text-xs text-red-600 mt-1">{esimError}</div> : null}
      </div>
    </fieldset>
  );
}
